import { Component, OnChanges, OnDestroy, OnInit, inject, computed, signal, effect, untracked, input, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { AiHeadlinesComponent } from '../../../../shared/components/ai-headlines/ai-headlines.component';
import { DatePickerComponent, DateRangePickerOutput, TimeUnit } from '../../../../shared/components/date-picker/date-picker.component';
import { ContractCountStatsComponent } from './contract-count-stats/contract-count-stats.component';
import { PaymentCollectionCardsComponent } from './payment-collection-cards/payment-collection-cards.component';
import { StateContractsComponent } from './state-contracts/state-contracts.component';
import { ContractStatusComponent } from './contract-status/contract-status.component';
import { ApiService } from '../../../../core/services/api.service';
import { ProductConfigService } from '../../../../core/services/product-config.service';
import { PixelValue } from '../../../../shared/components/toggle-btns/toggle-btns.component';
import { DropdownComponent, DropdownOption } from '../../../../shared/components/ui/dropdown/dropdown.component';
import {
  CdkDragDrop,
  DragDropModule,
} from '@angular/cdk/drag-drop';
import { LayoutEditorOverlayDirective } from '../../../../shared/components/dashboard/layout-editor-overlay.directive';
import {
  applyLayoutEditorAutoWidthUpdates,
  applyLayoutEditorCardDrop,
  computeLayoutEditorAutoWidthUpdates,
  reorderLayoutEditorItemsOnVisibilityToggle,
} from '../../../../shared/components/dashboard/dashboard-layout-editor.helpers';
import { DashboardPageKey } from '../../../../core/enums/dashboard-cards.enum';
import { CustomDashboardPinService } from '../../../../core/services/custom-dashboard-pin.service';
import { DashboardLayoutService } from '../../../../core/services/dashboard-layout.service';
import { CustomizeLayoutBatchService } from '../../../../core/services/customize-layout-batch.service';
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import {
  createEmbeddedSectionFilterByWidgetKeyMap,
  findCatalogPageId,
  resolveActiveCatalogProductId,
} from '../../../../shared/components/dashboard/dashboard-embed.helpers';
import { IconComponentV2 } from '../../../../shared/components/icon-component/icon.component';
import { CURRENCY_DROPDOWN_OPTIONS, getCurrencyInfoByCocuId } from '../../../../shared/utils/country-currency-options.util';

/**
 * Section ids for the customizable Contracts Report dashboard.
 * Defined locally so adding the Customize Layout / Pin to Custom Dashboard
 * UI here does NOT touch any shared enum / catalog files (purely additive
 * change beyond the page-key + catalog-hints entries).
 *
 * NOTE on granularity: each id maps to exactly ONE visible card on screen,
 * so the Customize Layout popup mirrors the rendered dashboard 1:1. The
 * platform / contract sections are flattened: each underlying
 * `platformKeys` / `contractKeys` entry gets its own id and grid slot via
 * `[onlyKeys]` on `<app-payment-collection-cards>`.
 */
export enum ContractsReportSectionId {
  CONTRACT_COUNT_STATS                 = 'contract_count_stats',
  CONTRACT_STATUS                      = 'contract_status',
  STATE_CONTRACTS                      = 'state_contracts',

  // Platform cards (rendered via `platformKeys`)
  PLATFORM_TOP_CONTRACTS_PLATFORMS     = 'platform_top_contracts_platforms',
  PLATFORM_AVERAGE_CONTRACT_VALUE      = 'platform_average_contract_value',

  // Contract cards (rendered via `contractKeys`)
  CONTRACT_ENROLLMENT_COLLECTED        = 'contract_enrollment_collected',
  CONTRACT_UPFRONT_COLLECTED           = 'contract_upfront_collected',
  CONTRACT_LATE_FEE_COLLECTED          = 'contract_late_fee_collected',
  CONTRACT_CHANGE_PAYMENT_DATE         = 'contract_change_payment_date',
  CONTRACT_DOWNPAYMENT_COLLECTED       = 'contract_downpayment_collected',
  CONTRACT_EDIT_CONTRACT               = 'contract_edit_contract',
  CONTRACT_REVERSALS                   = 'contract_reversals',
  CONTRACT_REFUNDS                     = 'contract_refunds',
  CONTRACT_LATE_FEE_REFUNDED           = 'contract_late_fee_refunded',
  CONTRACT_SETTLED_CONTRACTS           = 'contract_settled_contracts',
  CONTRACT_STATE                       = 'contract_state',
  CONTRACT_PROTECTED_PENDING           = 'contract_protected_pending',
  CONTRACT_PROTECTED_CONTRACTS         = 'contract_protected_contracts',
  CONTRACT_NON_PROTECTED_CONTRACTS     = 'contract_non_protected_contracts',
}

interface ContractsReportSectionDef {
  id: ContractsReportSectionId;
  title: string;
  /**
   * For sections backed by `<app-payment-collection-cards>`, the underlying
   * `cards` array source — used only for type narrowing in the template.
   */
  source?: 'platform' | 'contract';
  /**
   * Specific `card.key` to render via `[onlyKeys]` for sections backed by
   * `<app-payment-collection-cards>`.
   */
  cardKey?: string;
}

const CONTRACTS_REPORT_SECTION_DEF: ContractsReportSectionDef[] = [
  { id: ContractsReportSectionId.CONTRACT_COUNT_STATS,             title: 'Contract Count Stats' },
  { id: ContractsReportSectionId.CONTRACT_STATUS,                  title: 'Contract Status' },
  { id: ContractsReportSectionId.STATE_CONTRACTS,                  title: 'State Contracts' },

  // Platform-backed cards — order mirrors `platformKeys`.
  { id: ContractsReportSectionId.PLATFORM_TOP_CONTRACTS_PLATFORMS, title: 'Top Contracts Platforms', source: 'platform', cardKey: 'platform_contracts' },
  { id: ContractsReportSectionId.PLATFORM_AVERAGE_CONTRACT_VALUE,  title: 'Average Contract Value',  source: 'platform', cardKey: 'average_financed_recurring' },

  // Contract-backed cards — order mirrors `contractKeys`.
  { id: ContractsReportSectionId.CONTRACT_ENROLLMENT_COLLECTED,    title: 'Enrollment Collected',                  source: 'contract', cardKey: 'enrollment_collection' },
  { id: ContractsReportSectionId.CONTRACT_UPFRONT_COLLECTED,       title: 'Upfront Collected',                     source: 'contract', cardKey: 'upfront_collection' },
  { id: ContractsReportSectionId.CONTRACT_LATE_FEE_COLLECTED,      title: 'Late Fee Collected',                    source: 'contract', cardKey: 'late_fee_collection' },
  { id: ContractsReportSectionId.CONTRACT_CHANGE_PAYMENT_DATE,     title: 'Change Payment Date Fee Collected',     source: 'contract', cardKey: 'change_payment_date_collection' },
  { id: ContractsReportSectionId.CONTRACT_DOWNPAYMENT_COLLECTED,   title: 'Downpayment Collected',                 source: 'contract', cardKey: 'downpayment_collection' },
  { id: ContractsReportSectionId.CONTRACT_EDIT_CONTRACT,           title: 'Edit Contract',                         source: 'contract', cardKey: 'contract_edit_records' },
  { id: ContractsReportSectionId.CONTRACT_REVERSALS,               title: 'Reversals',                             source: 'contract', cardKey: 'reversals' },
  { id: ContractsReportSectionId.CONTRACT_REFUNDS,                 title: 'Refunds',                               source: 'contract', cardKey: 'refunds' },
  { id: ContractsReportSectionId.CONTRACT_LATE_FEE_REFUNDED,       title: 'Late Fee Refunded',                     source: 'contract', cardKey: 'late_fee_refunded' },
  { id: ContractsReportSectionId.CONTRACT_SETTLED_CONTRACTS,       title: 'Settled Contracts',                     source: 'contract', cardKey: 'settled_contracts' },
  { id: ContractsReportSectionId.CONTRACT_STATE,                   title: 'Contract State',                        source: 'contract', cardKey: 'contract_stats_new_1' },
  { id: ContractsReportSectionId.CONTRACT_PROTECTED_PENDING,       title: 'Protected Status Pending',              source: 'contract', cardKey: 'fetch_protected_pending_contracts' },
  { id: ContractsReportSectionId.CONTRACT_PROTECTED_CONTRACTS,     title: 'Protected Contracts',                   source: 'contract', cardKey: 'fetch_protected_marked_contracts' },
  { id: ContractsReportSectionId.CONTRACT_NON_PROTECTED_CONTRACTS, title: 'Non-protected Contracts',               source: 'contract', cardKey: 'fetch_nonProtected_marked_contracts' },
];

/** Persisted layout state per section (visibility + width + order). */
interface SectionLayoutItem {
  id: ContractsReportSectionId;
  visible: boolean;
  /** 4 | 6 | 8 | 12 — proportional width inside the 12-col dashboard grid. */
  colSpan: number;
  order: number;
}

/** Draft row used by the inline Customize Layout editor. */
interface LayoutEditorItem extends SectionLayoutItem {
  title: string;
}

export interface CardConfig {
  heading: string;
  shortHeading: string;
  key: string;
  viewIndex: number;
  cardIndex: number;
  colSpan?: number;
  primaryLineColor?: string;
  valueKey: string;
  show?: boolean;
  showTable?: boolean;
  showDedicatedHeading?: boolean;
  showToggles?: boolean;
  showlabels?: boolean;
  showToggleButton?: boolean;
  showStaticsToggleButton?: boolean;
  showProgress?: boolean;
  showHeading?: boolean;
  defaultView?: 'chart' | 'table';
  statsKeys: {
    left: {
      tooltip: string;
      previoustooltip: string;
      key: string;
      valueType: 'currency' | 'number' | string[];
      prefix?: string;
      valueTypeIndex?: number[];
    };
  };
  cardType?: 1 | 2 | 3;
  showDetails?: boolean;
  height: PixelValue | 'fit-content';
  headers?: any;
  toggleIconPath?: string;
  showIcon?: boolean;
}

@Component({
  standalone: true,
  selector: 'app-contracts-report',
  imports: [
    DatePickerComponent,
    ContractCountStatsComponent,
    ContractStatusComponent,
    PaymentCollectionCardsComponent,
    StateContractsComponent,
    DropdownComponent,
    FormsModule,
    CommonModule,
    AiHeadlinesComponent,
    DragDropModule,
    LayoutEditorOverlayDirective,
    IconComponentV2,
  ],
  templateUrl: './contracts-report.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [`
    /* Equalize section card heights inside the customizable 12-col grid.
       The outer grid already uses items-stretch, so each section's column
       wrapper (.group.relative.h-full.flex.flex-col) stretches to the
       tallest sibling in its row. However, the embedded <app-*> elements
       (and their root card <div>s/<section>s) are not block-level /
       100% height by default — and \`<app-cards>\` (used for every col-4
       line-chart tile) has a hard-coded \`h-[400px]\` on its root
       \`<section>\`. Both issues together make narrower col-4 / col-6
       cards visually shorter than a col-8 / col-12 neighbour on the same
       row.

       These rules force every embedded section host, its top-level card,
       and (critically) the inner \`<app-cards>\` section to fill the
       available wrapper height. Templates and TS logic are untouched and
       scope is constrained to this component via \`:host\`, so other
       pages using \`<app-cards>\` are unaffected. */

    /* 1) Make every embedded section's host element behave as a block
          that fully fills the column wrapper above it. */
    :host app-contract-count-stats,
    :host app-contract-status,
    :host app-state-contracts,
    :host app-payment-collection-cards,
    :host ::ng-deep app-contract-count-stats,
    :host ::ng-deep app-contract-status,
    :host ::ng-deep app-state-contracts,
    :host ::ng-deep app-payment-collection-cards {
      display: block !important;
      width: 100% !important;
      height: 100% !important;
      min-height: 100% !important;
    }

    /* 2) Stretch the immediate root <div> rendered inside each child
          component (its outer card / grid wrapper) to the full host
          height so the card's background / border reaches the bottom
          of the row. */
    :host ::ng-deep app-contract-status > div,
    :host ::ng-deep app-state-contracts > div,
    :host ::ng-deep app-payment-collection-cards > div {
      height: 100% !important;
      min-height: 100% !important;
      width: 100%;
    }

    /* 3) contract-count-stats has an extra wrapper layer:
          <app-contract-count-stats>
            <div class="col-span-12">          <-- needs h-100%
              <div class="grid ... bg-white">  <-- the actual card, needs h-100%
       Both layers must stretch so the card background fills the row. */
    :host ::ng-deep app-contract-count-stats > div.col-span-12,
    :host ::ng-deep app-contract-count-stats > div.col-span-12 > .grid {
      height: 100% !important;
      min-height: 100% !important;
      width: 100%;
    }

    /* 4) THE ACTUAL FIX for the col-4 line-chart tiles.
          \`payment-collection-cards\` renders cardType-1 tiles through the
          shared \`<app-cards>\` component, whose root <section> is hard-coded
          to \`h-[400px]\`. That fixed height is what makes "Late Fee",
          "Enrollment Collected", "Change Payment Date Fee" etc. look
          shorter than the neighbouring col-8 cards (which are content-
          sized to 450–550px+).

          We override only that one inner section, scoped to instances
          rendered inside contracts-report, so the card stretches with
          its row. The chart inside already uses \`h-full\` and grows to
          fill the new height. */
    :host ::ng-deep app-payment-collection-cards app-cards {
      display: block !important;
      width: 100% !important;
      height: 100% !important;
      min-height: 100% !important;
    }
    :host ::ng-deep app-payment-collection-cards app-cards:has(app-scroll-table) {
      height: 400px !important;
      min-height: 400px !important;
    }
    :host ::ng-deep app-payment-collection-cards app-cards > section.cards-component {
      height: 100% !important;
      min-height: 100% !important;
    }
    :host ::ng-deep app-payment-collection-cards app-cards:has(app-scroll-table) > section.cards-component {
      height: 400px !important;
      min-height: 400px !important;
    }
  `]
})
export class ContractsReportComponent implements OnInit, OnChanges, OnDestroy {
  private apiService = inject(ApiService);
  private productConfigService = inject(ProductConfigService);
  private customDashboardPin = inject(CustomDashboardPinService);
  private dashboardLayoutService = inject(DashboardLayoutService);
  private _customizeLayoutBatch = inject(CustomizeLayoutBatchService);

  readonly mixedMode = input<boolean>(false);
  readonly visibleWidgetKeys = input<string[] | null>(null);
  readonly catalogPageId = input<number | null>(null);

  /**
   * Catalog `pages.id` for this report — resolved from the backend
   * dashboard catalog using {@link DashboardPageKey.CONTRACTS_REPORT}.
   * Required by `customDashboardPin.toggleSection(...)` to attach pinned
   * sections to the user's mixed (My Custom) dashboard. Stays `null`
   * when the catalog has no entry — in that case the pin button silently
   * no-ops, so the rest of the page is unaffected.
   */
  readonly effectiveCatalogPageId = signal<number | null>(null);

  /** Backend dashboard-catalog key used to resolve `effectiveCatalogPageId`. */
  readonly PAGE_KEY = DashboardPageKey.CONTRACTS_REPORT;

  selectedProductId = signal<string | null>(this.apiService.selectedProductId());
  productConfig = computed(() => {
    const productId =
      this.selectedProductId() ?? this.apiService.selectedProductId();
    const productName = productId
      ? this.productConfigService.getProductName(productId)
      : 'denefits';
    return this.productConfigService.getProductConfig(productName);
  });

  currencyOptions: DropdownOption[] = [
    { label: 'United States', shortLabel: 'USD', description: 'US DOLLARS', value: '1', image: 'assets/img/country-flags/America.svg', badge: { text: '$' } },
    { label: 'Canada', shortLabel: 'CAD', description: 'CANADA DOLLARS', value: '3', image: 'assets/img/country-flags/Canada.svg', badge: { text: '$' } },
    { label: 'Australia', shortLabel: 'AUD', description: 'AUSTRALIA DOLLARS', value: '2', image: 'assets/img/country-flags/Australia.svg', badge: { text: '$' } },
    { label: 'New Zealand', shortLabel: 'NZD', description: 'NEW ZEALAND DOLLARS', value: '8', image: 'assets/img/country-flags/Australia.svg', badge: { text: '$' } },
    { label: 'Germany', shortLabel: 'EUR', description: 'EURO', value: '9', image: 'assets/img/country-flags/Germany.svg', badge: { text: '€' } },
    { label: 'Italy', shortLabel: 'EUR', description: 'EURO', value: '10', image: 'assets/img/country-flags/Italy.svg', badge: { text: '€' } },
    { label: 'France', shortLabel: 'EUR', description: 'EURO', value: '11', image: 'assets/img/country-flags/France.svg', badge: { text: '€' } },
    { label: 'Spain', shortLabel: 'EUR', description: 'EURO', value: '12', image: 'assets/img/country-flags/Spain.svg', badge: { text: '€' } },
    { label: 'Japan', shortLabel: 'YEN', description: 'YEN', value: '15', image: 'assets/img/country-flags/Japan.svg', badge: { text: '¥' } },
    { label: 'United Kingdom', shortLabel: 'GBP', description: 'POUND', value: '5', image: 'assets/img/country-flags/United Kingdom.svg', badge: { text: '£' } },
    { label: 'United Kingdom', shortLabel: 'EUR', description: 'EURO', value: '4', image: 'assets/img/country-flags/United Kingdom.svg', badge: { text: '€' } },
    { label: 'Poland', shortLabel: 'PLN', description: 'POLISH ZLOTY', value: '14', image: 'assets/img/country-flags/Poland.svg', badge: { text: 'zł' } },
    { label: 'Poland', shortLabel: 'EUR', description: 'EURO', value: '13', image: 'assets/img/country-flags/Poland.svg', badge: { text: '€' } },
  ];

  selectedCurrency = signal<string>('1');

  selectedOption = computed(() => {
    return this.currencyOptions.find(opt => opt.value === this.selectedCurrency());
  });

  selectedCurrencyInfo = computed(() => {
    const val = this.selectedCurrency();
    const mapping: Record<string, { cocu_id: number; country_id: number }> = {
      '1': { cocu_id: 1, country_id: 1 }, // US Dollars (United States)
      '2': { cocu_id: 2, country_id: 7 }, // Australia Dollar (Australia)
      '3': { cocu_id: 3, country_id: 2 }, // Canada Dollar (Canada)
      '4': { cocu_id: 4, country_id: 5 }, // Euro (United Kingdom)
      '5': { cocu_id: 5, country_id: 5 }, // Pound (United Kingdom)
      '8': { cocu_id: 8, country_id: 9 }, // New Zealand Dollar (New Zealand)
      '9': { cocu_id: 9, country_id: 10 }, // Euro (Germany)
      '10': { cocu_id: 10, country_id: 11 }, // Euro (Italy)
      '11': { cocu_id: 11, country_id: 12 }, // Euro (France)
      '12': { cocu_id: 12, country_id: 13 }, // Euro (Spain)
      '13': { cocu_id: 13, country_id: 14 }, // Euro (Poland)
      '14': { cocu_id: 14, country_id: 14 }, // Polish Zloty (Poland)
      '15': { cocu_id: 15, country_id: 15 }, // Yen (Japan)
    };
    return mapping[val] || mapping['1'];
  });


    dateRange: DateRangePickerOutput = {
      start_date: new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split('T')[0],
      end_date: new Date().toISOString().split('T')[0],
      previous_start_date: new Date(new Date().setDate(new Date().getDate() - 60)).toISOString().split('T')[0],
      previous_end_date: new Date(new Date().setDate(new Date().getDate() - 31)).toISOString().split('T')[0],
      unit: 'day',
      comparison_type: 'previous-period'
    };

    selectedContractType: number[] = [1, 2, 3, 4, 5, 6, 7, 41];
    
    platformHeaders: any[] = [
      { label: 'Platform', key: 'name', showIcon: true, iconKey: 'icon' },
      {
        label: 'Current',
        key: 'value',
        showProgress: false,
        showTooltip: false,
      },
      {
        label: 'Previous',
        key: 'value',
        isPrevious: true,
        showTooltip: false,
      },
    ];

  readonly contractKeys: CardConfig[] = [
    {
      heading: 'Enrollment Collected',
      shortHeading: 'Enrollment',
      key: 'enrollment_collection',
      viewIndex: 0,
      cardIndex: 0,
      colSpan: 1,
      primaryLineColor: '#00CFDE',
      valueKey: 'amount',
      show: true,
      showStaticsToggleButton: true,
      showDedicatedHeading: true,
      showToggleButton: true,
      showToggles: true,
      showlabels: false,
      statsKeys: {
        left: {
          tooltip: 'stats.from_contracts',
          previoustooltip: 'previousstats.from_contracts',
          key: 'amount',
          valueType: 'currency',
          prefix: '$',
        },
      },
      cardType: 1,
      showDetails: true,
      height: '380px',
    },
    // Last three
    {
      heading: 'Upfront collected',
      shortHeading: 'Upfront',
      key: 'upfront_collection',
      viewIndex: 1,
      cardIndex: 1,
      colSpan: 1,
      primaryLineColor: '#1474FF',
      valueKey: 'amount',
      show: true,
      showStaticsToggleButton: true,
      showDedicatedHeading: true,
      showToggleButton: true,
      showToggles: true,
      showlabels: false,
      statsKeys: {
        left: {
          tooltip: 'stats.from_contracts',
          previoustooltip: 'previousstats.from_contracts',
          key: 'amount',
          valueType: 'currency',
          prefix: '$',
        },
      },
      cardType: 1,
      showDetails: true,
      height: '380px',
    },
    {
      heading: 'Late Fee Collected',
      shortHeading: 'Late Fee',
      key: 'late_fee_collection',
      viewIndex: 2,
      cardIndex: 2,
      colSpan: 1,
      primaryLineColor: '#0C66E8',
      valueKey: 'amount',
      show: true,
      showStaticsToggleButton: true,
      showDedicatedHeading: true,
      showToggles: true,
      showlabels: false,
      statsKeys: {
        left: {
          tooltip: 'stats.from_contracts',
          previoustooltip: 'previousstats.from_contracts',
          key: 'amount',
          valueType: 'currency',
          prefix: '$',
        },
      },
      cardType: 1,
      showDetails: true,
      height: '380px',
    },
    {
      heading: 'Change Payment Date Fee Collected',
      shortHeading: 'Change Payment Date Fee',
      key: 'change_payment_date_collection',
      viewIndex: 3,
      cardIndex: 3,
      colSpan: 1,
      primaryLineColor: '#3DE195',
      valueKey: 'amount',
      show: true,
      showStaticsToggleButton: true,
      showDedicatedHeading: true,
      showToggleButton: true,
      showToggles: true,
      showlabels: false,
      statsKeys: {
        left: {
          tooltip: 'stats.from_contracts',
          previoustooltip: 'previousstats.from_contracts',
          key: 'amount',
          valueType: 'currency',
          prefix: '$',
        },
      },
      cardType: 1,
      showDetails: true,
      height: '380px',
    },
    // Last three
    {
      heading: 'Downpayment Collected',
      shortHeading: 'Downpayment',
      key: 'downpayment_collection',
      viewIndex: 4,
      cardIndex: 4,
      colSpan: 1,
      primaryLineColor: '#D433FF',
      valueKey: 'amount',
      show: true,
      showStaticsToggleButton: true,
      showDedicatedHeading: true,
      showToggleButton: true,
      showToggles: true,
      showlabels: false,
      statsKeys: {
        left: {
          tooltip: 'stats.from_contracts',
          previoustooltip: 'previousstats.from_contracts',
          key: 'amount',
          valueType: 'currency',
          prefix: '$',
        },
      },
      cardType: 1,
      showDetails: true,
      height: '380px',
    },
    {
      heading: 'Edit Contract',
      shortHeading: 'Edit Contract',
      key: 'contract_edit_records',
      viewIndex: 5,
      cardIndex: 5,
      colSpan: 1,
      primaryLineColor: '#FFBF47',
      valueKey: 'editContractCount',
      show: true,
      showStaticsToggleButton: true,
      showDedicatedHeading: true,
      showToggles: true,
      showlabels: false,
      statsKeys: {
        left: {
          tooltip: 'stats.from_contracts',
          previoustooltip: 'previousstats.from_contracts',
          key: 'editContractCount',
          valueType: 'number',
          prefix: '',
        },
      },
      cardType: 1,
      showDetails: true,
      height: '380px',
    },
    {
      heading: 'Reversals',
      shortHeading: 'Reversals',
      key: 'reversals',
      viewIndex: 6,
      cardIndex: 6,
      colSpan: 1,
      primaryLineColor: '#3DE195',
      valueKey: 'amount',
      show: true,
      showStaticsToggleButton: true,
      showDedicatedHeading: true,
      showToggleButton: true,
      showToggles: true,
      showlabels: false,
      statsKeys: {
        left: {
          tooltip: 'stats.from_contracts',
          previoustooltip: 'previousstats.from_contracts',
          key: 'amount',
          valueType: 'currency',
          prefix: '$',
        },
      },
      cardType: 1,
      showDetails: true,
      height: '380px',
    },
    // three
    {
      heading: 'Refunds',
      shortHeading: 'Refunds',
      key: 'refunds',
      viewIndex: 7,
      cardIndex: 7,
      colSpan: 1,
      primaryLineColor: '#BE9D60',
      valueKey: 'amount',
      show: true,
      showStaticsToggleButton: true,
      showDedicatedHeading: true,
      showToggles: true,
      showlabels: false,
      statsKeys: {
        left: {
          tooltip: 'stats.from_contracts',
          previoustooltip: 'previousstats.from_contracts',
          key: 'amount',
          valueType: 'currency',
          prefix: '$',
        },
      },
      cardType: 1,
      showDetails: true,
      height: '380px',
    },
    {
      heading: 'Late Fee Refunded',
      shortHeading: 'Late Fee Refunded',
      key: 'late_fee_refunded',
      viewIndex: 8,
      cardIndex: 8,
      colSpan: 1,
      primaryLineColor: '#BE9D60',
      valueKey: 'amount',
      show: true,
      showStaticsToggleButton: true,
      showDedicatedHeading: true,
      showToggles: true,
      showlabels: false,
      statsKeys: {
        left: {
          tooltip: 'stats.from_contracts',
          previoustooltip: 'previousstats.from_contracts',
          key: 'amount',
          valueType: 'currency',
          prefix: '$',
        },
      },
      cardType: 1,
      showDetails: true,
      height: '380px',
    },
    {
      heading: 'Settled Contracts',
      shortHeading: 'Settled Contracts',
      key: 'settled_contracts',
      viewIndex: 9,
      cardIndex: 9,
      colSpan: 1,
      primaryLineColor: '#F05050',
      valueKey: 'count',
      show: true,
      showStaticsToggleButton: true,
      showDedicatedHeading: false,
      showToggles: true,
      showlabels: false,
      showToggleButton: true,
      statsKeys: {
        left: {
          tooltip: 'stats.from_contracts',
          previoustooltip: 'previousstats.from_contracts',
          key: 'count',
          valueType: 'number',
          prefix: '$',
        },
      },
      cardType: 1,
      showDetails: true,
      height: '380px',
    },
    {
      heading: 'Contract State',
      shortHeading: 'Contract State',
      key: 'contract_stats_new_1',
      viewIndex: 10,
      cardIndex: 10,
      colSpan: 2,
      primaryLineColor: '#F05050',
      valueKey: 'value',
      show: true,
      showStaticsToggleButton: false,
      showDedicatedHeading: false,
      showToggleButton: false,
      showToggles: false,
      showlabels: false,
      showProgress: true,
      defaultView: 'table',
      statsKeys: {
        left: {
          tooltip: 'stats.from_contracts',
          previoustooltip: 'previousstats.from_contracts',
          key: 'value',
          valueType: 'number',
          prefix: '',
        },
      },
      showTable: true,
      showHeading: true,
      cardType: 1,
      showDetails: true,
      height: '390px',
      headers: [
        { label: 'Name', key: 'name', align: 'text-left' },
        {
          label: 'Current',
          key: 'value',
          align: 'text-left',
          showTooltip: false,
          showInlineProgress: true,
        },
        {
          label: 'Previous',
          key: 'value',
          align: 'text-left',
          isPrevious: true,
          showProgress: true,
          showTooltip: false,
        },
      ],
    },
    // Last three
    {
      heading: 'Protected Status Pending',
      shortHeading: 'Protected Status Pending',
      key: 'fetch_protected_pending_contracts',
      viewIndex: 11,
      cardIndex: 11,
      colSpan: 1,
      primaryLineColor: '#FFBF47',
      valueKey: 'count',
      show: true,
      showStaticsToggleButton: true,
      showDedicatedHeading: false,
      showToggleButton: true,
      showToggles: true,
      showlabels: false,
      statsKeys: {
        left: {
          tooltip: 'stats.from_contracts',
          previoustooltip: 'previousstats.from_contracts',
          key: 'count',
          valueType: 'number',
          prefix: '$',
        },
      },
      cardType: 1,
      showDetails: true,
      height: '380px',
    },
    {
      heading: 'Protected Contracts',
      shortHeading: 'Protected Contracts',
      key: 'fetch_protected_marked_contracts',
      viewIndex: 12,
      cardIndex: 12,
      colSpan: 1,
      primaryLineColor: '#3DE195',
      valueKey: 'count',
      show: true,
      showStaticsToggleButton: true,
      showDedicatedHeading: false,
      showToggleButton: true,
      showToggles: true,
      showlabels: false,
      statsKeys: {
        left: {
          tooltip: 'stats.from_contracts',
          previoustooltip: 'previousstats.from_contracts',
          key: 'count',
          valueType: 'number',
          prefix: '$',
        },
      },
      cardType: 1,
      showDetails: true,
      height: '380px',
    },
    {
      heading: 'Non-protected Contracts',
      shortHeading: 'Non-protected Contracts',
      key: 'fetch_nonProtected_marked_contracts',
      viewIndex: 13,
      cardIndex: 13,
      colSpan: 1,
      primaryLineColor: '#F05050',
      valueKey: 'count',
      show: true,
      showStaticsToggleButton: true,
      showDedicatedHeading: false,
      showToggleButton: true,
      showToggles: true,
      showlabels: false,
      statsKeys: {
        left: {
          tooltip: 'stats.from_contracts',
          previoustooltip: 'previousstats.from_contracts',
          key: 'count',
          valueType: 'number',
          prefix: '$',
        },
      },
      cardType: 1,
      showDetails: true,
      height: '380px',
    },
  ];

  readonly platformKeys: CardConfig[] = [
    {
      heading: 'Top Contracts Platforms ',
      shortHeading: 'Top Contracts Platforms ',
      key: 'platform_contracts',
      viewIndex: 1,
      cardIndex: 1,
      show: true,
      showStaticsToggleButton: false,
      showDedicatedHeading: false,
      showToggleButton: false,
      showToggles: false,
      showlabels: false,
      statsKeys: {
        left: {
          tooltip: 'stats.from_contracts',
          previoustooltip: 'previousstats.from_contracts',
          key: 'value',
          valueType: 'number',
          prefix: '$',
        },
      },
      showTable: true,
      primaryLineColor: '#F05050',
      valueKey: 'value',
      showProgress: false,
      cardType: 2,
      showDetails: false,
      height: '295px',
      showIcon: true,
      toggleIconPath: 'assets/img/PNG/fxemoji_bolt.png'
    },
    {
      heading: 'Average Contract Value',
      shortHeading: 'Average Contract Value',
      key: 'average_financed_recurring',
      viewIndex: 2,
      cardIndex: 2,
      show: true,
      showStaticsToggleButton: false,
      showDedicatedHeading: false,
      showToggleButton: false,
      showToggles: false,
      showlabels: false,
      statsKeys: {
        left: {
          tooltip: 'stats.from_contracts',
          previoustooltip: 'previousstats.from_contracts',
          key: 'value',
          valueType: ['currency', 'currency', 'number'],
          prefix: '$',
        },
      },
      showTable: true,
      primaryLineColor: '#F05050',
      valueKey: 'value',
      showProgress: false,
      cardType: 3,
      showDetails: false,
      height: '140px',
    },
  ];

    constructor() {
    // React to global product selection changes
    effect(() => {
      const globalProductId = this.apiService.selectedProductId();
      if (globalProductId) {
        untracked(() => {
          this.selectedProductId.set(globalProductId);
        });
      }
    });

    // ─── Resolve backend catalog `pages.id` for "Pin to Custom Dashboard"
    effect(() => {
      const fromParent = this.catalogPageId();
      if (fromParent != null) {
        untracked(() => this.effectiveCatalogPageId.set(fromParent));
        return;
      }
      const productId = this.apiService.selectedProductId();
      const productPid = this.apiService.selectedProductPid();
      if (!productId && productPid == null) return;
      const activeProductPid = resolveActiveCatalogProductId(this.apiService);
      untracked(() => {
        this.dashboardLayoutService.getCatalogProducts().subscribe((catalog) => {
          const pageId = findCatalogPageId(catalog as any[], this.PAGE_KEY, activeProductPid);
          this.effectiveCatalogPageId.set(pageId);
        });
      });
    });

    // ─── Hydrate which sections are already pinned on the user's mixed
    // (My Custom) dashboard so the green check / "Added" pill renders
    // correctly after a page refresh. Re-runs whenever the catalog
    // page id flips.
    effect(() => {
      if (this.mixedMode()) return;
      const pageId = this.effectiveCatalogPageId();
      if (pageId == null) return;
      untracked(() => {
        this.customDashboardPin.hydratePinnedWidgetKeys(pageId).subscribe((keys) => {
          const pinned = new Set<ContractsReportSectionId>();
          keys.forEach((k) => {
            const mapped =
              this.WIDGET_KEY_TO_SECTION.get(k) ??
              this.WIDGET_KEY_TO_SECTION.get(this._stripProductPrefix(k));
            if (mapped != null) {
              pinned.add(mapped);
              return;
            }
            if (Object.values(ContractsReportSectionId).includes(
                k as ContractsReportSectionId,
            )) {
              pinned.add(k as ContractsReportSectionId);
            }
          });
          this.pinnedSections.set(pinned);
        });
      });
    });

    // ─── Pre-resolve catalog `widgets.id` per section so per-click pins
    // can skip the catalog round-trip (mirrors the Campaign Ads / Social
    // Platform / Business Performance Report pattern). When the
    // resolved map has an entry for the clicked section,
    // `addToCustomDashboard` forwards the numeric `widgets.id` straight
    // to {@link CustomDashboardPinService.toggleLayoutItem} (one
    // POST/PATCH per click — never the whole page). Sections whose id
    // isn't in the map fall back to the existing `toggleSection` flow
    // so the page's click-to-pin behaviour stays preserved.
    effect(() => {
      const pageId = this.effectiveCatalogPageId();
      const productId = resolveActiveCatalogProductId(this.apiService);
      if (pageId == null || productId == null) {
        untracked(() => this.pinnableWidgetIds.set(new Map()));
        return;
      }
      untracked(() => {
        this.customDashboardPin
          .resolveCatalogWidgetIdsForPage(pageId, productId)
          .subscribe((widgetIdMap) => {
            const next = new Map<ContractsReportSectionId, number>();
            for (const sec of Object.values(ContractsReportSectionId)) {
              const mappedKey = this.SECTION_WIDGET_KEYS[sec];
              const id =
                (mappedKey ? widgetIdMap.get(mappedKey) : null) ??
                widgetIdMap.get(sec);
              if (id != null) next.set(sec, id);
            }
            this.pinnableWidgetIds.set(next);
        });
      });
    });
  }
  
    ngOnInit(): void {
      // Initialize platform headers with default date range
      this.updatePlatformHeaders();
      this._initLayout();
    }

    ngOnChanges(): void {
      // Handle changes here
    }

    ngOnDestroy(): void {
      // Cleanup logic here
    }

    rangeValues(event: any) {
     
    }
  
    private cdr = inject(ChangeDetectorRef);

    onDateRangeChange(event: DateRangePickerOutput) {
     
      // event contains: start_date, end_date, previous_start_date, previous_end_date, unit
      
      // Example usage:
      this.dateRange = event;
      this.cdr.detectChanges();
      
      // Update platform headers with new date ranges
      this.updatePlatformHeaders();
      
      // Use the data for API calls or other logic
      if (this.dateRange.previous_start_date && this.dateRange.previous_end_date) {
       
      } else {
       
      }
    }

    private updatePlatformHeaders(): void {
      this.platformHeaders = [
        { label: 'Platform', key: 'name', showIcon: true, iconKey: 'icon' },
        {
          label: `Current (${this.formatDateRange(this.dateRange.start_date, this.dateRange.end_date)})`,
          key: 'value',
          showProgress: false,
          showTooltip: false,
        },
        {
          label: `Previous (${this.formatDateRange(this.dateRange.previous_start_date || '', this.dateRange.previous_end_date || '')})`,
          key: 'value',
          isPrevious: true,
          showTooltip: false,
        },
      ];
    }

    formatDateRange(startDate: string, endDate: string): string {
      if (!startDate || !endDate) return '';
      
      const start = new Date(startDate);
      const end = new Date(endDate);
      
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      
      const startMonth = monthNames[start.getMonth()];
      const endMonth = monthNames[end.getMonth()];
      const startDay = start.getDate();
      const endDay = end.getDate();
      
      // If same month, show "Aug 10 - Nov 10", otherwise "Aug 10 - Nov 10"
      if (start.getMonth() === end.getMonth()) {
        return `${startMonth} ${startDay} - ${endDay}`;
      }
      
      return `${startMonth} ${startDay} - ${endMonth} ${endDay}`;
    }

  // ─── Customize Layout (additive — does not affect existing data flow) ──
  readonly SectionId = ContractsReportSectionId;
  private readonly SECTIONS = CONTRACTS_REPORT_SECTION_DEF;

  /** Allowed col-span steps the user can cycle through per card. */
  private readonly COL_SPAN_STEPS: ReadonlyArray<number> = [4, 6, 8, 12];

  /** localStorage key for this page's persisted layout. */
  private readonly LAYOUT_LS_KEY = 'prism_contracts_report_layout_v2';

  /**
   * Default col-span per section — mirrors the original visual layout:
   *  - Contract Count Stats / Contract Status / State Contracts → 12 (full row).
   *  - Platform cards (`grid-cols-2` originally) → 6/12 each (2 per row).
   *  - Contract cards (`md:grid-cols-3` originally) → 4/12 each (3 per row),
   *    except Contract State which had `colSpan: 2` (66%) → 8/12.
   * Rows resolve to:
   *   row1: enrollment + upfront + late_fee = 4+4+4 = 12 ✓
   *   row2: change_payment + downpayment + edit_contract = 4+4+4 = 12 ✓
   *   row3: reversals + refunds + late_fee_refunded = 4+4+4 = 12 ✓
   *   row4: settled + contract_state = 4+8 = 12 ✓
   *   row5: protected_pending + protected + non_protected = 4+4+4 = 12 ✓
   */
  private readonly DEFAULT_COL_SPAN: Record<ContractsReportSectionId, number> = {
    [ContractsReportSectionId.CONTRACT_COUNT_STATS]:             12,
    [ContractsReportSectionId.CONTRACT_STATUS]:                  12,
    [ContractsReportSectionId.STATE_CONTRACTS]:                  12,

    // Platform cards — 2 per row.
    [ContractsReportSectionId.PLATFORM_TOP_CONTRACTS_PLATFORMS]: 6,
    [ContractsReportSectionId.PLATFORM_AVERAGE_CONTRACT_VALUE]:  6,

    // Contract cards — 3 per row (Contract State spans 2/3 = 8/12).
    [ContractsReportSectionId.CONTRACT_ENROLLMENT_COLLECTED]:    4,
    [ContractsReportSectionId.CONTRACT_UPFRONT_COLLECTED]:       4,
    [ContractsReportSectionId.CONTRACT_LATE_FEE_COLLECTED]:      4,
    [ContractsReportSectionId.CONTRACT_CHANGE_PAYMENT_DATE]:     4,
    [ContractsReportSectionId.CONTRACT_DOWNPAYMENT_COLLECTED]:   4,
    [ContractsReportSectionId.CONTRACT_EDIT_CONTRACT]:           4,
    [ContractsReportSectionId.CONTRACT_REVERSALS]:               4,
    [ContractsReportSectionId.CONTRACT_REFUNDS]:                 4,
    [ContractsReportSectionId.CONTRACT_LATE_FEE_REFUNDED]:       4,
    [ContractsReportSectionId.CONTRACT_SETTLED_CONTRACTS]:       4,
    [ContractsReportSectionId.CONTRACT_STATE]:                   8,
    [ContractsReportSectionId.CONTRACT_PROTECTED_PENDING]:       4,
    [ContractsReportSectionId.CONTRACT_PROTECTED_CONTRACTS]:     4,
    [ContractsReportSectionId.CONTRACT_NON_PROTECTED_CONTRACTS]: 4,
  };

  /**
   * Maps each frontend {@link ContractsReportSectionId} to the canonical
   * (product-agnostic) backend `widget_key` suffix from the
   * `visionboard-contracts` catalog. Values omit the 2-char product
   * prefix (`de_`, `cr_`, …) — same pattern as Monthly Report 2 /
   * Business Performance Report:
   *
   *   Denefits  → `de_vb_contract_status_timeline`  (pages.id = 42)
   *   Credee    → `cr_vb_contract_status_timeline`  (own pages.id)
   *
   * {@link CustomDashboardPinService} and
   * {@link CustomizeLayoutBatchService} resolve the correct numeric
   * `widgets.id` per active product via exact match or the
   * `widget_key.endsWith('_' + key)` suffix rule.
   */
  private readonly SECTION_WIDGET_KEYS: Record<ContractsReportSectionId, string> = {
    [ContractsReportSectionId.CONTRACT_COUNT_STATS]:             'vb_contracts_created_today',
    [ContractsReportSectionId.CONTRACT_STATUS]:                  'vb_contract_status_timeline',
    [ContractsReportSectionId.STATE_CONTRACTS]:                  'vb_state_wise_businesses',

    [ContractsReportSectionId.PLATFORM_TOP_CONTRACTS_PLATFORMS]: 'vb_top_contracts_platforms',
    [ContractsReportSectionId.PLATFORM_AVERAGE_CONTRACT_VALUE]:  'vb_average_contract_value',

    [ContractsReportSectionId.CONTRACT_ENROLLMENT_COLLECTED]:    'vb_enrollment_collected_trend',
    [ContractsReportSectionId.CONTRACT_UPFRONT_COLLECTED]:       'vb_upfront_collected_trend',
    [ContractsReportSectionId.CONTRACT_LATE_FEE_COLLECTED]:      'vb_late_fee_collected_trend',
    [ContractsReportSectionId.CONTRACT_CHANGE_PAYMENT_DATE]:     'vb_payment_date_fee_trend',
    [ContractsReportSectionId.CONTRACT_DOWNPAYMENT_COLLECTED]:   'vb_downpayment_collected',
    [ContractsReportSectionId.CONTRACT_EDIT_CONTRACT]:           'vb_edit_contract',
    [ContractsReportSectionId.CONTRACT_REVERSALS]:               'vb_reversals_trend',
    [ContractsReportSectionId.CONTRACT_REFUNDS]:                 'vb_refunds',
    [ContractsReportSectionId.CONTRACT_LATE_FEE_REFUNDED]:       'vb_late_fee_refunded',
    [ContractsReportSectionId.CONTRACT_SETTLED_CONTRACTS]:       'vb_settled_contracts',
    [ContractsReportSectionId.CONTRACT_STATE]:                   'vb_contract_state_resolution',
    [ContractsReportSectionId.CONTRACT_PROTECTED_PENDING]:       'vb_protected_status_pending',
    [ContractsReportSectionId.CONTRACT_PROTECTED_CONTRACTS]:     'vb_protected_contracts_trend',
    [ContractsReportSectionId.CONTRACT_NON_PROTECTED_CONTRACTS]: 'vb_non_protected_contracts',
  };

  /** Reverse lookup: canonical / raw backend `widget_key` → section id. */
  private readonly WIDGET_KEY_TO_SECTION: ReadonlyMap<string, ContractsReportSectionId> =
    new Map(
      (Object.entries(this.SECTION_WIDGET_KEYS) as [
        ContractsReportSectionId,
        string,
      ][]).map(([sec, key]) => [key, sec]),
    );

  /** Persisted layout map driving the actual rendered page. */
  readonly layoutMap = signal<Record<ContractsReportSectionId, SectionLayoutItem>>(
    {} as Record<ContractsReportSectionId, SectionLayoutItem>,
  );

  /** All sections sorted by their persisted order. */
  readonly orderedSections = computed(() =>
    Object.values(this.layoutMap()).slice().sort((a, b) => a.order - b.order),
  );

  /** Sections currently visible, in order — used by the live grid. */
  readonly orderedVisibleSections = computed(() =>
    this.orderedSections().filter((s) => s.visible),
  );

  readonly embeddedVisibleSections = createEmbeddedSectionFilterByWidgetKeyMap(
    this.orderedVisibleSections,
    this.mixedMode,
    this.visibleWidgetKeys,
    this.WIDGET_KEY_TO_SECTION,
    this.orderedSections,
  );

  /** Tailwind col-span class for a section in the live 12-col grid. */
  getSectionColClass(item: SectionLayoutItem | ContractsReportSectionId): string {
    const id = typeof item === 'string' ? item : item.id;
    const span = this._normalizeColSpan(this.layoutMap()[id]?.colSpan ?? 12);
    switch (span) {
      case 4: return 'col-span-12 lg:col-span-4';
      case 6: return 'col-span-12 lg:col-span-6';
      case 8: return 'col-span-12 lg:col-span-8';
      case 12:
      default: return 'col-span-12';
    }
  }

  trackSection = (_idx: number, sec: SectionLayoutItem): string => sec.id;

  // ─── Customize Layout editor (mini-dashboard preview) ──────────────────
  readonly isLayoutEditorOpen = signal(false);
  readonly editorItems = signal<LayoutEditorItem[]>([]);

  readonly editorVisibleCount = computed(
    () => this.editorItems().filter((i) => i.visible).length,
  );

  trackEditorItem = (_idx: number, item: LayoutEditorItem): string => item.id;

  openCustomizeLayout(): void {
    const titleById = new Map(this.SECTIONS.map((s) => [s.id, s.title]));
    const items: LayoutEditorItem[] = this.orderedSections().map((s) => ({
      id: s.id,
      title: titleById.get(s.id) ?? s.id,
      visible: s.visible,
      colSpan: this._normalizeColSpan(s.colSpan),
      order: s.order,
    }));
    this.editorItems.set(items);
    this.isLayoutEditorOpen.set(true);
  }

  closeLayoutEditor(): void {
    this.isLayoutEditorOpen.set(false);
  }

  toggleEditorCardVisibility(id: ContractsReportSectionId): void {
    this.editorItems.update((items) =>
      reorderLayoutEditorItemsOnVisibilityToggle(items, id),
    );
  }

  resetLayoutEditorToDefaults(): void {
    const defaults: LayoutEditorItem[] = this.SECTIONS.map((s, idx) => ({
      id: s.id,
      title: s.title,
      visible: true,
      colSpan: this._normalizeColSpan(this.DEFAULT_COL_SPAN[s.id] ?? 12),
      order: idx,
    }));
    this.editorItems.set(defaults);
  }

  applyLayoutEditor(): void {
    const draft = this.editorItems();
    const layout: Record<ContractsReportSectionId, SectionLayoutItem> =
      {} as Record<ContractsReportSectionId, SectionLayoutItem>;
    draft.forEach((d, idx) => {
      layout[d.id] = {
        id: d.id,
        visible: d.visible,
        colSpan: this._normalizeColSpan(d.colSpan),
        order: idx,
      };
    });
    this.layoutMap.set(layout);
    this._saveLayoutToStorage(layout);

    // Best-effort backend persistence — see {@link _persistLayoutToBackend}.
    // The local state above is unchanged regardless of whether the
    // network call succeeds, so the existing functionality / UI is
    // preserved exactly as before.
    this._persistLayoutToBackend(draft);

    this.isLayoutEditorOpen.set(false);
  }

  /**
   * Writes the editor draft to the user's per-page personalized
   * dashboard via the shared {@link CustomizeLayoutBatchService} —
   * same flow Campaign Ads Stats / Monthly Report 1 / 2 / Business
   * Performance Report use.
   *
   * Two paths so the API call always fires regardless of whether
   * the reactive catalog effect in the constructor has hydrated:
   *
   *   • **Fast path** — `effectiveCatalogPageId()` already cached
   *     (catalog GET completed before the user clicked Apply). Goes
   *     straight to the `…ByKeys` service variant which forkJoins
   *     the catalog + per-page dashboard load and resolves widget
   *     ids from whichever responds with usable data.
   *
   *   • **Slow / fallback path** — pageId not yet cached. Triggers
   *     `getCatalogProducts()` on demand, resolves the page id
   *     synchronously inside the click, caches it for next click,
   *     then hands off to the same service variant.
   *
   * Errors are silently swallowed by the service so the local
   * state (layoutMap + localStorage) already applied above is
   * never reverted by a network failure.
   */
  private _persistLayoutToBackend(
    draft: ReadonlyArray<LayoutEditorItem>,
  ): void {
    const cachedPageId = this.effectiveCatalogPageId();
    const cachedWidgetMap = this.pinnableWidgetIds();
    const productId = resolveActiveCatalogProductId(this.apiService);

    const toBackendKeysDraft = (resolvedPageId: number) =>
      draft.map((d, idx) => ({
        key: this.SECTION_WIDGET_KEYS[d.id] ?? String(d.id),
        sourcePageId: resolvedPageId,
        colSpan: this._normalizeColSpan(d.colSpan),
        visible: d.visible,
        customConfig: null,
        order: idx,
      }));

    if (cachedPageId != null && cachedWidgetMap.size > 0) {
      const batchDraft = draft
        .filter((d) => cachedWidgetMap.has(d.id))
        .map((d, idx) => ({
          widgetId: cachedWidgetMap.get(d.id) ?? 0,
          sourcePageId: cachedPageId,
          colSpan: this._normalizeColSpan(d.colSpan),
          visible: d.visible,
          customConfig: null,
          order: idx,
        }));

      if (batchDraft.length) {
        this._customizeLayoutBatch.applyEditorDraftToPageDashboard({
          pageId: cachedPageId,
          productId,
          fallbackSourcePageId: cachedPageId,
          draft: batchDraft,
        });
        return;
      }
    }

    this.dashboardLayoutService
      .getCatalogProducts()
      .pipe(catchError(() => of([] as any[])))
      .subscribe((catalog) => {
        const resolvedPageId =
          cachedPageId ??
          findCatalogPageId(catalog as any[], this.PAGE_KEY, productId);

        if (resolvedPageId == null) {
          this._warnCatalogMiss(catalog as any[], productId);
          return;
        }

        if (cachedPageId == null) {
          this.effectiveCatalogPageId.set(resolvedPageId);
        }

        this._customizeLayoutBatch.applyEditorDraftToPageDashboardByKeys({
          pageId: resolvedPageId,
          productId,
          fallbackSourcePageId: resolvedPageId,
          draft: toBackendKeysDraft(resolvedPageId),
        });
      });
  }

  /**
   * Diagnostic warning printed when {@link findCatalogPageId} can't
   * resolve a `pages.id` for this report. Dumps the list of page
   * names the catalog actually advertises for the active product so
   * the developer can either:
   *
   *   • Align the backend page name with the configured hints in
   *     `CATALOG_PAGE_HINTS[CONTRACTS_REPORT]`, or
   *   • Add a new hint variant that matches the backend name.
   *
   * Stays scoped to this component (does not touch shared helpers)
   * so the existing behaviour of every other dashboard is preserved.
   */
  private _warnCatalogMiss(
    catalog: any[],
    productId: string | number | null,
  ): void {
    const scopedProducts =
      productId != null && String(productId).trim() !== ''
        ? (catalog ?? []).filter(
            (p: any) => String(p?.id) === String(productId),
          )
        : (catalog ?? []);

    const productsForLog =
      scopedProducts.length > 0 ? scopedProducts : (catalog ?? []);

    const pageNames: string[] = [];
    for (const product of productsForLog) {
      for (const page of product?.pages ?? []) {
        const name = String(page?.name ?? '').trim();
        if (name) pageNames.push(name);
      }
    }

    // eslint-disable-next-line no-console
    console.warn(
      `[ContractsReport] No catalog page found for ` +
        `"${this.PAGE_KEY}" (active product=${productId}). ` +
        `Catalog advertised pages for this product: ` +
        `${pageNames.length ? pageNames.join(', ') : '(none)'}. ` +
        `If the correct page is in the list above, add a matching ` +
        `substring to CATALOG_PAGE_HINTS[CONTRACTS_REPORT] in ` +
        `dashboard-embed.helpers.ts. If the list is empty, the ` +
        `backend has no catalog page for this report yet.`,
    );
  }

  /** Steps a card down one width. Respects each card's designed default as the lower bound. */
  decreaseEditorCardColSpan(id: ContractsReportSectionId): void {
    this.editorItems.update((items) =>
      items.map((i) => {
        if (i.id !== id) return i;
        const idx = this.COL_SPAN_STEPS.indexOf(this._normalizeColSpan(i.colSpan));
        if (idx <= 0) return i;
        const nextSpan = this.COL_SPAN_STEPS[idx - 1];
        if (this._isBelowDefaultColSpan(i.id, nextSpan)) return i;
        return { ...i, colSpan: nextSpan };
      }),
    );
  }

  increaseEditorCardColSpan(id: ContractsReportSectionId): void {
    this.editorItems.update((items) =>
      items.map((i) => {
        if (i.id !== id) return i;
        const idx = this.COL_SPAN_STEPS.indexOf(this._normalizeColSpan(i.colSpan));
        if (idx < 0 || idx >= this.COL_SPAN_STEPS.length - 1) return i;
        return { ...i, colSpan: this.COL_SPAN_STEPS[idx + 1] };
      }),
    );
  }

  autoAdjustEditorWidths(): void {
    const updates = computeLayoutEditorAutoWidthUpdates(this.editorItems());
    if (!updates.size) return;
    this.editorItems.update((items) =>
      applyLayoutEditorAutoWidthUpdates(items, updates),
    );
  }

  canAutoAdjustEditorWidths(): boolean {
    return computeLayoutEditorAutoWidthUpdates(this.editorItems()).size > 0;
  }

  /**
   * Whether the minimize button should be active for this card. A card
   * cannot shrink below its designed default col-span — e.g. a card
   * designed for `col-8` can only step between 8 and 12.
   */
  canDecreaseEditorCardColSpan(item: LayoutEditorItem): boolean {
    const idx = this.COL_SPAN_STEPS.indexOf(this._normalizeColSpan(item.colSpan));
    if (idx <= 0) return false;
    if (this._isBelowDefaultColSpan(item.id, this.COL_SPAN_STEPS[idx - 1])) {
      return false;
    }
    return true;
  }

  /** Whether `span` is below the designed default col-span for `id`. */
  private _isBelowDefaultColSpan(id: ContractsReportSectionId, span: number): boolean {
    const defaultSpan = this.DEFAULT_COL_SPAN[id];
    if (defaultSpan == null) return false;
    return span < this._normalizeColSpan(defaultSpan);
  }

  canIncreaseEditorCardColSpan(item: LayoutEditorItem): boolean {
    const idx = this.COL_SPAN_STEPS.indexOf(this._normalizeColSpan(item.colSpan));
    return idx >= 0 && idx < this.COL_SPAN_STEPS.length - 1;
  }

  getEditorColSpanClass(span: number): string {
    switch (this._normalizeColSpan(span)) {
      case 4:  return 'col-span-4';
      case 6:  return 'col-span-6';
      case 8:  return 'col-span-8';
      case 12: return 'col-span-12';
      default: return 'col-span-12';
    }
  }

  getEditorColSpanLabel(span: number): string {
    switch (this._normalizeColSpan(span)) {
      case 4:  return 'Small';
      case 6:  return 'Medium';
      case 8:  return 'Large';
      case 12: return 'Full';
      default: return `${span}`;
    }
  }

/* Swaps the dragged card with the card at the drop target so that ONLY those two tiles exchange positions in the array; every other card keeps its current array slot (no shuffle/cascade like CDK's default `moveItemInArray`). we changed the strategy from shift to pairwise-swap [event, { strategy: 'shift' });]*/
  onEditorCardDrop(event: CdkDragDrop<LayoutEditorItem[]>): void {
    const next = applyLayoutEditorCardDrop(this.editorItems(), event, {
      strategy: 'pairwise-swap',
    });
    if (next) this.editorItems.set(next);
  }
  // ─── Layout state private helpers ──────────────────────────────────────
  private _initLayout(): void {
    const stored = this._loadLayoutFromStorage();
    const layout: Record<ContractsReportSectionId, SectionLayoutItem> =
      {} as Record<ContractsReportSectionId, SectionLayoutItem>;

    this.SECTIONS.forEach((s, idx) => {
      const sid = s.id;
      const storedItem = stored?.[sid];
      layout[sid] = {
        id: sid,
        visible: storedItem?.visible ?? true,
        colSpan: this._normalizeColSpan(
          storedItem?.colSpan ?? this.DEFAULT_COL_SPAN[sid] ?? 12,
        ),
        order: storedItem?.order ?? idx,
      };
    });

    this.layoutMap.set(layout);
  }

  private _loadLayoutFromStorage(): Record<string, SectionLayoutItem> | null {
    try {
      const raw = localStorage.getItem(this.LAYOUT_LS_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  private _saveLayoutToStorage(
    layout: Record<ContractsReportSectionId, SectionLayoutItem>,
  ): void {
    try {
      localStorage.setItem(this.LAYOUT_LS_KEY, JSON.stringify(layout));
    } catch {
      // localStorage unavailable (e.g. private mode) — silently ignore.
    }
  }

  /**
   * Strips a leading 2-char product prefix (`de_`, `cr_`, …) so
   * pinned-widget hydration works regardless of which product is active.
   */
  private _stripProductPrefix(widgetKey: string): string {
    const match = /^[a-z]{2}_(.+)$/i.exec(widgetKey);
    return match ? match[1] : widgetKey;
  }

  private _normalizeColSpan(span: number | null | undefined): number {
    const allowed = [4, 6, 8, 12];
    if (span == null || !Number.isFinite(span)) return 12;
    if (allowed.includes(span)) return span;
    if (span <= 4) return 4;
    if (span <= 6) return 6;
    if (span <= 8) return 8;
    return 12;
  }

  // ─── "Pin to Custom Dashboard" (API-backed, mirrors Campaign Ads / SEO) ─
  /** Section ids that are currently pinned on the user's mixed dashboard. */
  readonly pinnedSections = signal<Set<ContractsReportSectionId>>(new Set());
  /** Sections with an in-flight pin/unpin request. Drives the spinner. */
  readonly pendingPinSections = signal<Set<ContractsReportSectionId>>(new Set());

  /**
   * Catalog `widgets.id` resolved per section, keyed by canonical
   * {@link ContractsReportSectionId}. Populated by the constructor
   * effect once the catalog products + page id resolve, and consumed
   * by {@link addToCustomDashboard} so per-click pins use the
   * pre-resolved id with
   * {@link CustomDashboardPinService.toggleLayoutItem} — one POST/PATCH
   * per click; never the whole page. Sections whose id isn't yet in
   * the map fall back to {@link CustomDashboardPinService.toggleSection}
   * so the page's click-to-pin behaviour is preserved while the
   * catalog is still loading.
   */
  readonly pinnableWidgetIds = signal<Map<ContractsReportSectionId, number>>(
    new Map(),
  );

  isAddedToCustomDashboard(
    sec: { id: ContractsReportSectionId } | ContractsReportSectionId,
  ): boolean {
    const sectionId = typeof sec === 'object' && sec !== null ? sec.id : sec;
    return this.pinnedSections().has(sectionId);
  }

  isAddingToCustomDashboard(
    sec: { id: ContractsReportSectionId } | ContractsReportSectionId,
  ): boolean {
    const sectionId = typeof sec === 'object' && sec !== null ? sec.id : sec;
    return this.pendingPinSections().has(sectionId);
  }

  /**
   * Toggles whether `sec` is pinned on the user's mixed (My Custom)
   * dashboard. Reuses the shared {@link CustomDashboardPinService} flow —
   * same path Monthly Report 1 / 2 / Campaign Ads / Social Platform /
   * SEO Stats / Business Performance Report use — so this page does
   * not introduce any new backend contract.
   *
   * Three paths so the click always does the right thing regardless
   * of whether the reactive catalog effect in the constructor has
   * hydrated yet:
   *   • **Fast path** — `effectiveCatalogPageId()` already cached AND
   *     `pinnableWidgetIds()` has the catalog `widgets.id` for this
   *     section. We forward the numeric id directly to
   *     {@link CustomDashboardPinService.toggleLayoutItem} (mirrors
   *     MR1 / MR2 — one POST/PATCH per click, never the whole page).
   *   • **Catalog-cached / widget-uncached path** — pageId cached but
   *     widget id not yet in the map. Hands off to
   *     {@link CustomDashboardPinService.toggleSection} which performs
   *     the catalog widget lookup on-click, preserving the page's
   *     pre-existing click-to-pin behaviour 1:1.
   *   • **Cold path** — pageId itself isn't cached yet (catalog GET
   *     still in flight, or the constructor effect's first run hasn't
   *     completed yet). Resolves the catalog on-click so the API
   *     still fires, mirroring the pattern used by Business
   *     Performance Report. The catalog response is normally cached
   *     by the layout service, so subsequent clicks hit the fast
   *     path above.
   */
  addToCustomDashboard(
    sec: { id: ContractsReportSectionId } | ContractsReportSectionId,
  ): void {
    const sectionId = typeof sec === 'object' && sec !== null ? sec.id : sec;
    if (!sectionId) return;
    if (this.pendingPinSections().has(sectionId)) return;

    const colSpan = this.layoutMap()[sectionId]?.colSpan ?? 12;
    const isCurrentlyPinned = this.isAddedToCustomDashboard(sectionId);
    const productId = resolveActiveCatalogProductId(this.apiService);
    if (productId == null) return;

    const onPinnedChange = (pinned: boolean) => {
        this.pinnedSections.update((s) => {
          const next = new Set(s);
          if (pinned) next.add(sectionId);
          else next.delete(sectionId);
          return next;
        });
    };

    const onPendingChange = (pending: boolean) => {
        this.pendingPinSections.update((s) => {
          const next = new Set(s);
          if (pending) next.add(sectionId);
          else next.delete(sectionId);
          return next;
        });
    };

    // Common "fire the pin" closure — forwards to the EXACT same
    // service Monthly Report 1 / 2 use via
    // `_customDashboardPin.toggleLayoutItem(...)`. When a numeric
    // `widgets.id` is already cached we forward it straight to
    // {@link CustomDashboardPinService.toggleLayoutItem}; otherwise
    // we fall back to {@link CustomDashboardPinService.toggleSection}
    // which performs the catalog lookup itself before firing the same
    // mixed-dashboard API (PATCH /dashboards/:id/widgets/:wid/visibility
    // when the mixed dashboard already exists, otherwise POST /dashboards
    // to seed it the first time).
    const fire = (resolvedPageId: number) => {
      const widgetId = this.pinnableWidgetIds().get(sectionId);
      if (widgetId != null) {
        this.customDashboardPin.toggleLayoutItem({
          pageId: resolvedPageId,
          widgetId,
          sourcePageId: resolvedPageId,
          colSpan,
          rowSpan: 1,
          customConfig: {},
          productId,
          isCurrentlyPinned,
          onPinnedChange,
          onPendingChange,
        });
        return;
      }

      this.customDashboardPin.toggleSection({
        pageId: resolvedPageId,
        widgetKey: this.SECTION_WIDGET_KEYS[sectionId] ?? sectionId,
        colSpan,
        isCurrentlyPinned,
        onPinnedChange,
        onPendingChange,
      });
    };

    // Fast path — catalog `pages.id` already cached by the constructor
    // effect. Fires the API immediately, no additional round-trips.
    const cachedPageId = this.effectiveCatalogPageId();
    if (cachedPageId != null) {
      fire(cachedPageId);
      return;
    }

    // Fallback / slow path — pageId not yet resolved. Resolve the
    // catalog on-click so the API still fires.
    this.dashboardLayoutService
      .getCatalogProducts()
      .pipe(catchError(() => of([] as any[])))
      .subscribe((catalog) => {
        const resolvedPageId = findCatalogPageId(
          catalog as any[],
          this.PAGE_KEY,
          productId,
        );

        if (resolvedPageId == null) {
          this._warnCatalogMiss(catalog as any[], productId);
          return;
        }

        // Cache so the fast path kicks in on the next click and the
        // existing hydration effect for "Added" pills stays consistent.
        this.effectiveCatalogPageId.set(resolvedPageId);

        // Resolve `widgets.id` map for this page so this click can
        // use the fast `toggleLayoutItem` variant. When the map
        // already has an entry from a prior effect run, this is
        // essentially free.
        if (this.pinnableWidgetIds().get(sectionId) == null) {
          this.customDashboardPin
            .resolveCatalogWidgetIdsForPage(resolvedPageId, productId)
            .subscribe((widgetIdMap) => {
              const next = new Map(this.pinnableWidgetIds());
              for (const id of Object.values(ContractsReportSectionId)) {
                const mappedKey = this.SECTION_WIDGET_KEYS[id];
                const wid =
                  (mappedKey ? widgetIdMap.get(mappedKey) : null) ??
                  widgetIdMap.get(id);
                if (wid != null) next.set(id, wid);
              }
              this.pinnableWidgetIds.set(next);
              fire(resolvedPageId);
            });
          return;
        }

        fire(resolvedPageId);
    });
  }

}
